import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  getNormalizedTableList,
  type NormalizedTableListItem,
} from '../../services/data-integration-api';

/**
 * 同步任务全局管理 — 列出所有规范存取表及同步状态
 */
export default function SyncGlobal() {
  const navigate = useNavigate();
  const [tables, setTables] = useState<NormalizedTableListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const result = await getNormalizedTableList({ page, pageSize });
      setTables(result.items);
      setTotal(result.total);
    } catch (e) {
      console.error('获取规范存取表列表失败:', e);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const creationStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      draft: '草稿',
      created: '已创建',
      failed: '创建失败',
    };
    return map[status] || status;
  };

  const creationStatusVariant = (status: string): 'default' | 'destructive' | 'secondary' => {
    if (status === 'created') return 'default';
    if (status === 'failed') return 'destructive';
    return 'secondary';
  };

  const syncStatusLabel = (status: string | null) => {
    const map: Record<string, string> = {
      completed: '同步成功',
      running: '同步中',
      failed: '同步失败',
      partial: '部分成功',
      pending: '等待中',
    };
    return status ? map[status] || status : '未同步';
  };

  const syncStatusVariant = (status: string | null): 'default' | 'destructive' | 'secondary' => {
    if (status === 'completed') return 'default';
    if (status === 'failed') return 'destructive';
    return 'secondary';
  };

  const formatTime = (ts: number | null) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('zh-CN');
  };

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <h2 className="text-base font-semibold">同步任务管理</h2>
      </div>

      <div className="p-6">
        {loading ? (
          <p className="text-muted-foreground">加载中...</p>
        ) : fetchError ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">加载失败，请重试</p>
            <Button onClick={fetchList}>重试</Button>
          </div>
        ) : tables.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">暂无规范存取表，请先完成字段映射并创建目标表</p>
            <Button onClick={() => navigate('/data-integration/connections')}>
              前往连接管理
            </Button>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>规范表名</TableHead>
                  <TableHead>源表</TableHead>
                  <TableHead>源库连接</TableHead>
                  <TableHead>创建状态</TableHead>
                  <TableHead>同步状态</TableHead>
                  <TableHead>最近同步</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tables.map(t => (
                  <TableRow key={t._id}>
                    <TableCell className="font-medium">{t.normalized_table_name}</TableCell>
                    <TableCell className="font-mono">{t.source_table_name}</TableCell>
                    <TableCell>{t.source_connection_name}</TableCell>
                    <TableCell>
                      <Badge variant={creationStatusVariant(t.creation_status)}>
                        {creationStatusLabel(t.creation_status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={syncStatusVariant(t.last_sync_status)}>
                        {syncStatusLabel(t.last_sync_status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatTime(t.last_sync_time)}
                    </TableCell>
                    <TableCell>
                      {t.creation_status === 'created' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/data-integration/sync/${t._id}`)}
                        >
                          管理同步
                        </Button>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {total > pageSize && (
              <div className="mt-4 flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  上一页
                </Button>
                <span className="text-sm text-muted-foreground">
                  第 {page} 页 / 共 {Math.ceil(total / pageSize)} 页
                </span>
                <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)}>
                  下一页
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
